import { useState, useEffect, useCallback } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useSearchParams } from 'react-router-dom';
import { LogOut, Loader2, AlertTriangle, LogIn } from 'lucide-react';
import { getMe, logout, User } from './services/api';
import Fixtures from './components/Fixtures';

/** Errors the auth Lambda can bounce back on ?error=. */
const LOGIN_ERRORS: Record<string, string> = {
  invalid_state: 'That sign-in attempt expired or did not match. Please try again.',
  missing_code: 'Google did not return a sign-in code. Please try again.',
  token_exchange_failed: 'Could not complete sign-in with Google. Please try again.',
  unauthorized_domain: 'Only @urbanzoo.io Google accounts can sign in.',
  server_error: 'Something went wrong signing in. Please try again.',
};

/**
 * Sign-in.
 *
 * This used to be a pixel copy of Central System's dark-slate screen, so the two apps
 * read as one system. It is now on the Urban Zoo brand instead — UZ Purple ground, white
 * card, Figtree — which means the two no longer match. That is a deliberate trade, made
 * when the brand guidelines were applied here first; Central System is the one that is
 * off-brand, not this.
 *
 * The logo is the full-colour lockup because it sits on a white card. It must not be
 * moved onto the purple: its UZ mark is #67247c, the same value as the ground, and the
 * mark would vanish inside its own keyline.
 */
function LoginPage() {
  const [params] = useSearchParams();
  const error = params.get('error');
  return (
    <div className="flex justify-center items-center min-h-screen bg-uz-purple p-4">
      <div className="bg-white p-8 sm:p-10 rounded-2xl shadow-xl text-center max-w-sm w-full animate-fade-in">
        <img src="/uz-lite.png" alt="Urban Zoo" className="h-11 mx-auto mb-8" />
        <h1 className="text-3xl mb-1">Events</h1>
        <p className="text-uz-muted text-base mb-8">Football fixtures and results</p>

        {error && (
          <div className="flex items-start gap-2.5 text-left bg-uz-wash border border-uz-edge rounded-xl p-3.5 mb-6">
            <AlertTriangle className="w-5 h-5 text-uz-purple shrink-0 mt-px" aria-hidden="true" />
            <p className="text-sm text-uz-black">{LOGIN_ERRORS[error] || 'Sign-in failed. Please try again.'}</p>
          </div>
        )}

        <a
          href="/auth/google"
          className="w-full flex items-center justify-center gap-2.5 bg-uz-purple hover:bg-uz-deep text-white font-bold py-3.5 px-6 rounded-xl transition-colors text-base"
        >
          <LogIn className="w-5 h-5" aria-hidden="true" />
          Sign in with Google
        </a>

        <p className="text-sm text-uz-muted mt-6">Urban Zoo Studios</p>
      </div>
    </div>
  );
}

function Shell({ user, onLogout }: { user: User; onLogout: () => void }) {
  return (
    <div className="min-h-screen flex flex-col bg-uz-cool">
      {/* White header, because the lockup is full colour and needs a light ground. The
          purple rule along the top is what carries the brand up here instead. */}
      <header className="sticky top-0 z-50 bg-white border-t-4 border-uz-purple border-b border-uz-edge/60">
        <div className="max-w-screen-2xl mx-auto px-4 sm:px-6 h-16 flex items-center justify-between gap-4">
          <img src="/uz-lite.png" alt="Urban Zoo" className="h-7 sm:h-8" />
          <div className="flex items-center gap-2 sm:gap-4 min-w-0">
            <span className="hidden sm:block text-sm text-uz-muted truncate">{user.email}</span>
            <button
              onClick={onLogout}
              aria-label="Sign out"
              className="flex items-center gap-2 px-3 py-2 rounded-lg text-sm text-uz-black hover:bg-uz-wash hover:text-uz-purple transition-colors"
            >
              <LogOut className="w-4 h-4" aria-hidden="true" />
              <span className="hidden sm:inline">Sign out</span>
            </button>
          </div>
        </div>
      </header>

      <main className="flex-1 w-full max-w-screen-2xl mx-auto px-4 sm:px-6 py-6 sm:py-8">
        <Fixtures />
      </main>

      <footer className="bg-uz-purple text-white text-center text-sm py-5 mt-12">
        © {new Date().getFullYear()} Urban Zoo Studios Ltd. All rights reserved.
      </footer>
    </div>
  );
}

export default function App() {
  const [user, setUser] = useState<User | null>(null);
  const [checking, setChecking] = useState(true);

  useEffect(() => {
    getMe().then(setUser).catch(() => setUser(null)).finally(() => setChecking(false));
  }, []);

  const handleLogout = useCallback(async () => {
    try { await logout(); } finally { setUser(null); }
  }, []);

  if (checking) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-uz-cool text-uz-muted">
        <Loader2 className="w-6 h-6 animate-spin" aria-label="Loading" />
      </div>
    );
  }

  return (
    <BrowserRouter>
      <Routes>
        <Route path="/login" element={user ? <Navigate to="/" replace /> : <LoginPage />} />
        <Route
          path="/"
          element={user ? <Shell user={user} onLogout={handleLogout} /> : <Navigate to="/login" replace />}
        />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </BrowserRouter>
  );
}
